<template>
    <div
        class="kv-file-dropzone"
        :class="[
            sizeClass,
            `kv-${variant}-input`,
            {
                dragging: isDragging,
                disabled,
                'kv-error': hasError,
            },
        ]"
        role="button"
        :tabindex="disabled ? -1 : 0"
        :aria-disabled="disabled ? 'true' : 'false'"
        @click="openFilePicker"
        @dragenter.prevent="handleDragEnter"
        @dragover.prevent="handleDragOver"
        @dragleave.prevent="handleDragLeave"
        @drop.prevent="handleDrop"
        @keydown.enter.prevent="openFilePicker"
        @keydown.space.prevent="openFilePicker"
    >
        <strong>{{ title }}</strong>
        <span>{{ description }}</span>
        <span v-if="buttonLabel" class="kv-file-dropzone-action">{{ buttonLabel }}</span>

        <input
            ref="fileInput"
            class="kv-file-dropzone-input"
            type="file"
            :accept="accept"
            :multiple="multiple"
            :disabled="disabled"
            @click.stop
            @change="handleFileChange"
        />
    </div>
</template>

<script>
export default {
    name: "KvFileDropzone",
    props: {
        title: {
            type: String,
            required: true,
        },
        description: {
            type: String,
            required: true,
        },
        buttonLabel: {
            type: String,
            default: "",
        },
        accept: {
            type: String,
            default: "",
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        variant: {
            type: String,
            default: "outlined",
        },
        size: {
            type: String,
            default: "regular",
        },
        hasError: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isDragging: false,
            dragCounter: 0,
        };
    },
    computed: {
        sizeClass() {
            return `kv-${this.size}-input`;
        },
    },
    methods: {
        openFilePicker() {
            if (this.disabled) return;
            this.$refs.fileInput.click();
        },
        handleDragEnter() {
            if (this.disabled) return;
            this.dragCounter += 1;
            this.isDragging = true;
        },
        handleDragOver(event) {
            if (this.disabled) return;

            if (event.dataTransfer) {
                event.dataTransfer.dropEffect = "copy";
            }

            this.isDragging = true;
        },
        handleDragLeave() {
            if (this.disabled) return;
            this.dragCounter = Math.max(0, this.dragCounter - 1);
            this.isDragging = this.dragCounter > 0;
        },
        handleDrop(event) {
            if (this.disabled) return;
            this.dragCounter = 0;
            this.isDragging = false;
            this.emitFiles(event.dataTransfer && event.dataTransfer.files);
        },
        handleFileChange(event) {
            this.emitFiles(event.target.files);
            event.target.value = "";
        },
        emitFiles(fileList) {
            if (!fileList) return;
            this.$emit("files", Array.from(fileList));
        },
    },
};
</script>

<style lang="less">
.kv-file-dropzone {
    align-items: center;
    appearance: none;
    background: #ffffff;
    border: 1px dashed #9da4ae;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 8px;
    justify-content: center;
    min-height: 146px;
    padding: 24px;
    text-align: center;
    transition:
        background 0.18s ease,
        border-color 0.18s ease,
        box-shadow 0.18s ease;
    width: 100%;

    &.kv-tinted-input {
        background: #f3faf4;
        border-color: #2aaa4c;
    }

    &.kv-outlined-input {
        background: #ffffff;
        border-color: #9da4ae;
    }

    &.dragging {
        background: #f0fdf4;
        border-color: #2aaa4c;
        box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.14);
    }

    &.disabled {
        background: #f9fafb;
        border-color: #d2d6db;
        cursor: not-allowed;
        opacity: 0.6;
        pointer-events: none;
    }

    &.kv-error {
        border-color: #f87171;
    }

    &.kv-small-input {
        min-height: 124px;
        padding: 18px 14px;

        strong {
            font-size: 16px;
        }
    }

    &.kv-large-input {
        min-height: 146px;
    }

    strong {
        color: #111927;
        font-family: "Sora", sans-serif;
        font-size: 18px;
        font-weight: 600;
        line-height: 1.3;
    }

    span {
        color: #6c737f;
        font-family: "Inter", sans-serif;
        font-size: 14px;
        font-weight: 400;
        line-height: 1.3;
        max-width: 100%;
        overflow-wrap: anywhere;
    }

    &:focus {
        outline: none;
    }

    &:focus-visible {
        box-shadow: 0 0 0 2px rgba(42, 170, 76, 0.14);
    }
}

.kv-file-dropzone-action {
    background: #fcfcfd;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    color: #1f2a37 !important;
    font-size: 16px !important;
    margin-top: 8px;
    max-width: 100%;
    padding: 8px 12px;
    overflow-wrap: anywhere;
}

.kv-file-dropzone-input {
    display: none;
}

@media (max-width: 767px) {
    .kv-file-dropzone {
        min-height: 132px;
        padding: 20px 16px;

        strong {
            overflow-wrap: anywhere;
        }
    }
}
</style>
